import {
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Loader,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  toast,
} from "@devhop/ui";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Cloud,
  Download,
  RefreshCw,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useRouterBack } from "@/shared/hooks/useRouterBack";
import type { SyncInboxStatus } from "../api/client";
import {
  useOpenShift,
  useResolveSyncInboxOrderMutation,
  useRetrySyncInboxOrderMutation,
  useSyncInboxOrders,
  useSyncQueueOverview,
} from "../api/queries";
import { usePOSContext } from "../context/POSContext";
import {
  listLocalCustomerDraftsForBranch,
  listLocalOrderOperationsForShift,
  listLocalOrdersForShift,
  listOfflineOrdersForShift,
  markLocalCustomerDraftSynced,
  markLocalOrderSyncStatus,
  retryLocalCustomerDraftSync,
} from "../lib/offline-sync/db";
import {
  formatCustomerDraftDisplay,
  getCustomerSyncStatusLabel,
  isCouponInboxConflict,
  isLocalOrderCouponConflict,
} from "../lib/offline-sync/local-first-display-helpers";
import type {
  LocalCustomerDraftRecord,
  LocalOrderOperationRecord,
  LocalOrderOperationType,
  LocalOrderRecord,
} from "../lib/offline-sync/types";
import { useOfflineSyncSummary } from "../lib/offline-sync/use-offline-sync-summary";
import { OfflineSyncDebugPanel } from "../ui/OfflineSyncDebugPanel";
import { PersistentStorageCard } from "../ui/PersistentStorageCard";

const STATUS_LABEL: Record<SyncInboxStatus, string> = {
  pending: "ລໍຖ້າ",
  processing: "ກຳລັງເຮັດ",
  completed: "ສຳເລັດ",
  failed: "ລົ້ມເຫຼວ",
  dead: "ຕ້ອງກວດ",
  resolved: "ແກ້ໄຂແລ້ວ",
};

const INBOX_PAGE_SIZE = 20;
const ADJUSTMENT_OPERATION_TYPES: LocalOrderOperationType[] = [
  "void_order",
  "refund_order",
  "move_table",
  "merge_orders",
];

function formatDateTime(value: string | Date | null): string {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("lo-LA");
}

function statusVariant(status: SyncInboxStatus) {
  if (status === "completed" || status === "resolved") return "default";
  if (status === "failed" || status === "dead") return "destructive";
  return "secondary";
}

export function SyncDebugPage() {
  const { selection } = usePOSContext();
  const branchId = selection?.branchId ?? null;
  const deviceId = selection?.deviceId ?? null;
  const goBack = useRouterBack();
  const [inboxPage, setInboxPage] = useState(1);
  const [adjustmentOperations, setAdjustmentOperations] = useState<
    LocalOrderOperationRecord[]
  >([]);
  const [customerDrafts, setCustomerDrafts] = useState<
    LocalCustomerDraftRecord[]
  >([]);
  const [localOrders, setLocalOrders] = useState<LocalOrderRecord[]>([]);
  const offlineSummary = useOfflineSyncSummary();
  const openShiftQuery = useOpenShift(branchId, deviceId);
  const shiftId = openShiftQuery.data?.id ?? null;
  const syncScopeKey = `${branchId ?? ""}:${deviceId ?? ""}:${shiftId ?? ""}`;
  const queueQuery = useSyncQueueOverview(branchId, deviceId, shiftId);
  const inboxQuery = useSyncInboxOrders({
    branchId,
    deviceId,
    shiftId,
    limit: INBOX_PAGE_SIZE,
    offset: (inboxPage - 1) * INBOX_PAGE_SIZE,
  });
  const retryMutation = useRetrySyncInboxOrderMutation();
  const resolveMutation = useResolveSyncInboxOrderMutation();

  useEffect(() => {
    void syncScopeKey;
    setInboxPage(1);
  }, [syncScopeKey]);

  useEffect(() => {
    if (!shiftId || !branchId) {
      setAdjustmentOperations([]);
      setCustomerDrafts([]);
      setLocalOrders([]);
      return;
    }

    let cancelled = false;
    const loadDebugData = async () => {
      const [operations, drafts, orders] = await Promise.all([
        listLocalOrderOperationsForShift(shiftId, {
          types: ADJUSTMENT_OPERATION_TYPES,
        }),
        listLocalCustomerDraftsForBranch(branchId),
        listLocalOrdersForShift(shiftId),
      ]);
      if (!cancelled) {
        setAdjustmentOperations(operations);
        setCustomerDrafts(drafts);
        setLocalOrders(orders);
      }
    };

    void loadDebugData();
    return () => {
      cancelled = true;
    };
  }, [shiftId, branchId]);

  const counts = useMemo(() => {
    const map = new Map<SyncInboxStatus, number>();
    for (const row of queueQuery.data?.countsByStatus ?? []) {
      map.set(row.status, row.value);
    }
    return map;
  }, [queueQuery.data]);

  const blockingCount =
    (counts.get("pending") ?? 0) +
    (counts.get("processing") ?? 0) +
    (counts.get("failed") ?? 0) +
    (counts.get("dead") ?? 0);
  const adjustmentBlockingCount = adjustmentOperations.filter((operation) =>
    ["queued", "syncing", "failed"].includes(
      operation.syncStatus ?? "not_ready",
    ),
  ).length;
  const couponConflictOrders = localOrders.filter(isLocalOrderCouponConflict);
  const unsyncedCustomerDrafts = customerDrafts.filter(
    (draft) => draft.syncStatus !== "synced",
  );

  const reloadDebugData = async () => {
    if (!shiftId || !branchId) return;
    const [operations, drafts, orders] = await Promise.all([
      listLocalOrderOperationsForShift(shiftId, {
        types: ADJUSTMENT_OPERATION_TYPES,
      }),
      listLocalCustomerDraftsForBranch(branchId),
      listLocalOrdersForShift(shiftId),
    ]);
    setAdjustmentOperations(operations);
    setCustomerDrafts(drafts);
    setLocalOrders(orders);
  };

  const handleRetryCustomerDraft = async (localCustomerId: string) => {
    await retryLocalCustomerDraftSync(localCustomerId);
    toast.success("ສົ່ງ customer draft ກັບເຂົ້າ queue ແລ້ວ");
    await reloadDebugData();
  };

  const handleMarkCustomerDraftSynced = async (
    draft: LocalCustomerDraftRecord,
  ) => {
    const serverCustomerId = window.prompt(
      "server customerId ຫຼັງກວດສອບແລ້ວ",
      draft.serverCustomerId ?? "",
    );
    if (!serverCustomerId?.trim()) return;
    await markLocalCustomerDraftSynced(
      draft.localCustomerId,
      serverCustomerId.trim(),
    );
    toast.success("mark customer draft sync ແລ້ວ");
    await reloadDebugData();
  };

  const handleResolveLocalCouponConflict = async (clientOrderId: string) => {
    const reason = window.prompt(
      "ເຫດຜົນທີ່ manager resolve coupon conflict",
      "ກວດສອບແລ້ວ resolve ດ້ວຍຕົນເອງ",
    );
    if (!reason) return;
    await markLocalOrderSyncStatus(clientOrderId, {
      status: "SYNCED",
      syncStatus: "synced",
      lastSyncError: undefined,
    });
    toast.success("resolve coupon conflict local ແລ້ວ");
    await reloadDebugData();
  };

  const handleRetry = (inboxId: string) => {
    retryMutation.mutate(inboxId, {
      onSuccess: () => toast.success("ສົ່ງກັບໄປລໍຖ້າ retry ແລ້ວ"),
      onError: (error) =>
        toast.error(
          error instanceof Error ? error.message : "retry sync order ບໍ່ສຳເລັດ",
        ),
    });
  };

  const handleResolve = (inboxId: string) => {
    const reason = window.prompt(
      "ເຫດຜົນທີ່ manager resolve order ນີ້",
      "ກວດສອບແລ້ວ resolve ດ້ວຍຕົນເອງ",
    );
    if (!reason) return;
    resolveMutation.mutate(
      { inboxId, reason },
      {
        onSuccess: () => toast.success("mark resolved ສຳເລັດ"),
        onError: (error) =>
          toast.error(
            error instanceof Error ? error.message : "mark resolved ບໍ່ສຳເລັດ",
          ),
      },
    );
  };

  const handleExportLocalOrders = async () => {
    if (!shiftId) {
      toast.error("ບໍ່ມີກະທີ່ເປີດຢູ່");
      return;
    }

    const records = await listOfflineOrdersForShift(shiftId, [
      "queued",
      "syncing",
      "failed",
      "needs_review",
      "synced",
    ]);
    const blob = new Blob([JSON.stringify(records, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `offline-orders-${shiftId}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!branchId || !deviceId) {
    return (
      <div className="container mx-auto max-w-5xl p-4">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Cloud className="h-8 w-8 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>ບໍ່ມີສາຂາ/ເຄື່ອງ POS</EmptyTitle>
            <EmptyDescription>ກະລຸນາເລືອກສາຂາແລະເຄື່ອງ POS ກ່ອນ</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  const inboxItems = inboxQuery.data?.items ?? [];
  const inboxTotal = inboxQuery.data?.total ?? 0;
  const inboxTotalPages = Math.max(1, Math.ceil(inboxTotal / INBOX_PAGE_SIZE));
  const inboxStart =
    inboxTotal === 0 ? 0 : (inboxPage - 1) * INBOX_PAGE_SIZE + 1;
  const inboxEnd = Math.min(inboxPage * INBOX_PAGE_SIZE, inboxTotal);

  return (
    <div className="container mx-auto max-h-[calc(100svh-10rem)] max-w-5xl space-y-4 overflow-y-auto p-4">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={goBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="font-bold text-2xl">Offline Sync Debug</h1>
          <p className="text-muted-foreground text-sm">
            ກວດ queue, retry, resolve ແລະ export order offline
          </p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">local ລໍຖ້າ</span>
            <Cloud className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="mt-2 font-bold text-2xl">
            {offlineSummary.pending}
          </div>
          <div className="mt-1 text-muted-foreground text-xs">
            queued {offlineSummary.queued} / ລົ້ມເຫຼວ {offlineSummary.failed} /
            review {offlineSummary.needsReview}
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">
              server ບລ໋ອກ
            </span>
            {blockingCount > 0 ? (
              <AlertTriangle className="h-4 w-4 text-destructive" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            )}
          </div>
          <div className="mt-2 font-bold text-2xl">{blockingCount}</div>
          <div className="mt-1 text-muted-foreground text-xs">
            ລົ້ມເຫຼວ {counts.get("failed") ?? 0} / dead {counts.get("dead") ?? 0}
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="text-muted-foreground text-sm">ບລ໋ອກເກົ່າສຸດ</div>
          <div className="mt-2 font-medium text-sm">
            {queueQuery.data?.oldestBlocking?.clientOrderId ?? "—"}
          </div>
          <div className="mt-1 text-muted-foreground text-xs">
            {formatDateTime(queueQuery.data?.oldestBlocking?.createdAt ?? null)}
          </div>
        </div>
      </div>

      <PersistentStorageCard />

      {branchId && deviceId && shiftId ? (
        <OfflineSyncDebugPanel
          scope={{ branchId, deviceId, shiftId }}
        />
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => {
            queueQuery.refetch();
            inboxQuery.refetch();
            void reloadDebugData();
          }}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          ໂຫຼດໃໝ່
        </Button>
        <Button variant="outline" onClick={handleExportLocalOrders}>
          <Download className="mr-2 h-4 w-4" />
          ສົ່ງອອກ local orders
        </Button>
      </div>

      <div className="rounded-lg border bg-card">
        <div className="border-b p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold">customer draft local</h2>
            <Badge
              variant={
                unsyncedCustomerDrafts.length > 0 ? "destructive" : "default"
              }
            >
              ຍັງບໍ່ sync {unsyncedCustomerDrafts.length}
            </Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            draft ລູກຄ້າທີ່ສ້າງ offline ແລະສະຖານະ sync
          </p>
        </div>
        {customerDrafts.length === 0 ? (
          <div className="p-6">
            <Empty>
              <EmptyHeader>
                <EmptyTitle>ບໍ່ມີ customer draft</EmptyTitle>
                <EmptyDescription>
                  ຍັງບໍ່ມີ local customer draft ໃນສາຂານີ້
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        ) : (
          <div className="divide-y">
            {customerDrafts.slice(0, 20).map((draft) => (
              <div key={draft.localCustomerId} className="space-y-2 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">
                        {formatCustomerDraftDisplay(draft)}
                      </span>
                      <Badge variant="outline">
                        {getCustomerSyncStatusLabel(draft.syncStatus)}
                      </Badge>
                    </div>
                    <div className="mt-1 font-mono text-muted-foreground text-xs">
                      {draft.localCustomerId}
                      {draft.serverCustomerId
                        ? ` · server ${draft.serverCustomerId}`
                        : ""}
                    </div>
                    {draft.lastSyncError && (
                      <div className="mt-2 rounded-md bg-destructive/10 p-2 text-destructive text-sm">
                        {draft.lastSyncError}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {draft.syncStatus !== "synced" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          void handleRetryCustomerDraft(draft.localCustomerId)
                        }
                      >
                        ລອງໃໝ່
                      </Button>
                    )}
                    {draft.syncStatus !== "synced" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          void handleMarkCustomerDraftSynced(draft)
                        }
                      >
                        mark synced ແລ້ວ
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-card">
        <div className="border-b p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold">Coupon replay / ຂັດແຍ້ງ</h2>
            <Badge
              variant={
                couponConflictOrders.length > 0 ? "destructive" : "default"
              }
            >
              local {couponConflictOrders.length}
            </Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            local orders ທີ່ coupon replay ລົ້ມເຫຼວ ຫຼື needs review
          </p>
        </div>
        {couponConflictOrders.length === 0 ? (
          <div className="p-6">
            <Empty>
              <EmptyHeader>
                <EmptyTitle>ບໍ່ມີ coupon conflict</EmptyTitle>
                <EmptyDescription>
                  local orders ໃນກະນີ້ sync coupon ໄດ້ປົກກະຕິ
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        ) : (
          <div className="divide-y">
            {couponConflictOrders.map((order) => (
              <div key={order.clientOrderId} className="space-y-2 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm">
                        #{order.clientOrderNo}
                      </span>
                      <Badge variant="destructive">{order.syncStatus}</Badge>
                    </div>
                    <div className="mt-1 text-muted-foreground text-xs">
                      {order.clientOrderId}
                    </div>
                    {order.couponSnapshots?.length ? (
                      <div className="mt-2 text-sm">
                        coupon:{" "}
                        {order.couponSnapshots
                          .map((snapshot) => snapshot.code)
                          .join(", ")}
                      </div>
                    ) : null}
                    {order.lastSyncError && (
                      <div className="mt-2 rounded-md bg-destructive/10 p-2 text-destructive text-sm">
                        {order.lastSyncError}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      void handleResolveLocalCouponConflict(
                        order.clientOrderId,
                      )
                    }
                  >
                    ແກ້ໄຂ
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-card">
        <div className="border-b p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold">adjustment operation local</h2>
            <Badge
              variant={adjustmentBlockingCount > 0 ? "destructive" : "default"}
            >
              ບລ໋ອກ {adjustmentBlockingCount}
            </Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            void/refund/move/merge operations ທີ່ລໍຖ້າ sync phase ຖັດໄປ
          </p>
        </div>
        {adjustmentOperations.length === 0 ? (
          <div className="p-6">
            <Empty>
              <EmptyHeader>
                <EmptyTitle>ບໍ່ມີ adjustment operation</EmptyTitle>
                <EmptyDescription>
                  ຍັງບໍ່ມີ void/refund/move/merge ໃນກະນີ້
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        ) : (
          <div className="divide-y">
            {adjustmentOperations.slice(0, 20).map((operation) => (
              <div key={operation.operationId} className="space-y-2 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm">
                        {operation.clientOrderId}
                      </span>
                      <Badge variant="secondary">{operation.type}</Badge>
                      <Badge
                        variant={
                          operation.syncStatus === "failed" ||
                          operation.syncStatus === "needs_review"
                            ? "destructive"
                            : "outline"
                        }
                      >
                        {operation.syncStatus ?? "not_ready"}
                      </Badge>
                    </div>
                    <div className="mt-1 text-muted-foreground text-xs">
                      operation {operation.operationId} · retry{" "}
                      {operation.retryCount ?? 0} · ບັນທຶກ{" "}
                      {formatDateTime(
                        operation.capturedAt ?? operation.createdAt,
                      )}
                    </div>
                    {operation.lastSyncError && (
                      <div className="mt-2 rounded-md bg-destructive/10 p-2 text-destructive text-sm">
                        {operation.lastSyncError}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-card">
        <div className="border-b p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold">server inbox</h2>
            <span className="text-muted-foreground text-xs">
              {inboxTotal > 0
                ? `${inboxStart}-${inboxEnd} / ${inboxTotal}`
                : "0 / 0"}
            </span>
          </div>
          <p className="text-muted-foreground text-sm">
            ລາຍການ order offline ທີ່ server ຮັບແລ້ວ
          </p>
        </div>
        {inboxQuery.isLoading ? (
          <div className="flex justify-center py-10">
            <span className="inline-flex size-8 animate-spin text-muted-foreground">
              <Loader />
            </span>
          </div>
        ) : inboxItems.length === 0 ? (
          <div className="p-6">
            <Empty>
              <EmptyHeader>
                <EmptyTitle>ບໍ່ມີ sync inbox</EmptyTitle>
                <EmptyDescription>
                  ຍັງບໍ່ມີ order offline ໃນ scope ນີ້
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        ) : (
          <div className="divide-y">
            {inboxItems.map((item) => (
              <div key={item.id} className="space-y-3 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm">
                        {item.clientOrderId}
                      </span>
                      <Badge variant={statusVariant(item.status)}>
                        {STATUS_LABEL[item.status]}
                      </Badge>
                    </div>
                    <div className="mt-1 text-muted-foreground text-xs">
                      job {item.jobId} · retry {item.retryCount} · ອັບເດດ{" "}
                      {formatDateTime(item.updatedAt)}
                    </div>
                    {item.serverOrderId && (
                      <div className="text-muted-foreground text-xs">
                        server order: {item.serverOrderId}
                      </div>
                    )}
                    {item.errorMessage && (
                      <div className="mt-2 rounded-md bg-destructive/10 p-2 text-destructive text-sm">
                        {item.errorCode}: {item.errorMessage}
                      </div>
                    )}
                    {item.replayResult && (
                      <div className="mt-2 rounded-md border bg-muted/30 p-2 text-sm">
                        <div className="font-medium text-xs">ຜົນ replay</div>
                        {item.replayResult.serverCustomerId && (
                          <div className="mt-1 text-muted-foreground text-xs">
                            serverCustomerId:{" "}
                            {item.replayResult.serverCustomerId}
                          </div>
                        )}
                        {item.replayResult.couponReplay?.length ? (
                          <ul className="mt-2 space-y-1 text-xs">
                            {item.replayResult.couponReplay.map((entry) => (
                              <li key={entry.code}>
                                {entry.code}:{" "}
                                {entry.applied
                                  ? `ໃຊ້ແລ້ວ -${entry.discountAmount ?? 0}`
                                  : entry.error ?? entry.errorCode ?? "ລົ້ມເຫຼວ"}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    )}
                    {isCouponInboxConflict(item) &&
                      (item.status === "failed" || item.status === "dead") && (
                        <div className="mt-2 text-amber-700 text-xs">
                          Coupon replay ຂັດແຍ້ງ — resolve ຫຼັງກວດສອບແລ້ວ
                        </div>
                      )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(item.status === "failed" ||
                      item.status === "dead" ||
                      item.status === "resolved") && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={retryMutation.isPending}
                        onClick={() => handleRetry(item.id)}
                      >
                        ລອງໃໝ່
                      </Button>
                    )}
                    {(item.status === "failed" || item.status === "dead") && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={resolveMutation.isPending}
                        onClick={() => handleResolve(item.id)}
                      >
                        ແກ້ໄຂ
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {inboxTotalPages > 1 && (
              <div className="p-4">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <span
                        className={
                          inboxPage === 1
                            ? "pointer-events-none opacity-50"
                            : "cursor-pointer"
                        }
                      >
                        <PaginationPrevious
                          onClick={() =>
                            setInboxPage((page) => Math.max(1, page - 1))
                          }
                        />
                      </span>
                    </PaginationItem>
                    {Array.from(
                      { length: inboxTotalPages },
                      (_, index) => index + 1,
                    ).map((pageNum) => {
                      if (
                        pageNum === 1 ||
                        pageNum === inboxTotalPages ||
                        (pageNum >= inboxPage - 1 && pageNum <= inboxPage + 1)
                      ) {
                        return (
                          <PaginationItem key={pageNum}>
                            <span className="cursor-pointer">
                              <PaginationLink
                                onClick={() => setInboxPage(pageNum)}
                                isActive={inboxPage === pageNum}
                              >
                                {pageNum}
                              </PaginationLink>
                            </span>
                          </PaginationItem>
                        );
                      }
                      if (
                        pageNum === inboxPage - 2 ||
                        pageNum === inboxPage + 2
                      ) {
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return null;
                    })}
                    <PaginationItem>
                      <span
                        className={
                          inboxPage === inboxTotalPages
                            ? "pointer-events-none opacity-50"
                            : "cursor-pointer"
                        }
                      >
                        <PaginationNext
                          onClick={() =>
                            setInboxPage((page) =>
                              Math.min(inboxTotalPages, page + 1),
                            )
                          }
                        />
                      </span>
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
